import React, { createContext, useContext, useState } from 'react'
import { Color, LogoOptions } from '../../../../core/models/entities/LogoOptions'
import { BaseLogo } from '../../../../core/models/entities/BaseLogo'

export interface LogoProviderContext {
  options: LogoOptions
  logoToEdit: BaseLogo | null
  setLogoToEdit: (logo: BaseLogo | null) => void
}

const LogoContext = createContext<LogoProviderContext | undefined>(undefined)

interface LogoProviderProps {
  children: React.ReactNode
  options?: LogoOptions
}

// Safe default while the options preload is still in flight (non-blocking preload):
// consumers see empty pickers for a moment instead of a crash, then real data propagates.
const EMPTY_OPTIONS: LogoOptions = {
  transferLogoTypes: [],
  transferMethods: [],
  transferMethodsNumbers: [],
  transferMethodsNames: [],
  colors: [],
  allColors: [],
  colorsEmbroidery: [],
  extensions: [],
  fonts: [],
  fontSizes: [],
  fontSizesNumbers: [],
  fontSizesNames: [],
  blockedMethodKeys: [],
  // Fail-open placeholders — real values arrive with the options preload.
  stockOrdersDisabled: false,
  stockOrdersEffectiveDisabled: false,
}

function LogoProvider({
  children,
  options: o,
}: LogoProviderProps): React.ReactElement {
  const [logoToEdit, setLogoToEdit] = useState<BaseLogo | null>(null)

  // Follow the prop instead of snapshotting it in state, so options arriving after
  // first render reach consumers instead of being frozen at mount.
  return (
    <LogoContext.Provider
      value={{ options: o ?? EMPTY_OPTIONS, logoToEdit, setLogoToEdit }}>
      {children}
    </LogoContext.Provider>
  )
}

function useLogoContext() {
  const context = useContext(LogoContext)
  if (!context) {
    throw new Error('useLogoContext must be used within a LogoProvider')
  }

  return context
}


export { LogoProvider, useLogoContext, LogoContext }
